// Resultados.js
import React, { useEffect, useMemo, useState } from 'react';
import { db, auth } from './firebaseConfig';
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { CSVLink } from 'react-csv';
import { format } from 'date-fns';
import { formatRecordingDuration } from './useInterviewRecorder';
import {
  getQuestionAnswerPlainText,
  getMatrixPayload,
  buildMatrixCsvColumnKey,
} from './matrixAnswerFormat';
import ResultadosDashboard from './ResultadosDashboard';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_RESULTS_PASSWORD || '';

const CSV_META_KEYS = [
  'Data/Hora',
  'Questionário',
  'Origem',
  'IP',
  'Email',
  'Cidade',
  'Estado',
  'País',
  'Endereço Completo',
  'Latitude',
  'Longitude',
  'Áudio (URL)',
  'Duração do áudio',
];

/** Descobre ordem das colunas (perguntas simples + uma coluna por linha de cada matriz). */
function collectCsvColumnPlan(rows) {
  const ordered = [];
  const seen = new Set();

  const addKey = (k) => {
    if (!k || seen.has(k)) return;
    seen.add(k);
    ordered.push(k);
  };

  rows.forEach((entry) => {
    entry.answers?.forEach((answer) => {
      answer.questions?.forEach((question) => {
        const mp = getMatrixPayload(question);
        if (mp && Array.isArray(mp.rows) && mp.rows.length > 0) {
          mp.rows.forEach((r, i) => {
            const rl = String(r.rowLabel ?? '').trim() || `Linha ${i + 1}`;
            addKey(buildMatrixCsvColumnKey(question.question, rl));
          });
        } else {
          addKey(question.question || '');
        }
      });
    });
  });

  return ordered;
}

function buildAnswerRow(entry, formatted) {
  return (answer) => {
    const email = entry.email;
    const location = entry.location;
    const latitude = location ? location.latitude : '';
    const longitude = location ? location.longitude : '';
    const city = location ? location.city : '';
    const state = location ? location.state : '';
    const country = location ? location.country : '';
    const address = location ? location.address : '';
    const ip = entry.ip;

    const record = {
      'Data/Hora': formatted,
      Questionário:
        answer.nome != null && String(answer.nome).trim() !== ''
          ? String(answer.nome).trim()
          : '',
      Origem: entry.origem || '',
      IP: ip,
      Email: email,
      Cidade: city,
      Estado: state,
      País: country,
      'Endereço Completo': address,
      Latitude: latitude,
      Longitude: longitude,
      'Áudio (URL)': entry.audioUrl || '',
      'Duração do áudio':
        entry.audioDuration != null ? formatRecordingDuration(entry.audioDuration) : '',
    };

    answer.questions?.forEach((question) => {
      const mp = getMatrixPayload(question);
      if (mp && Array.isArray(mp.rows) && mp.rows.length > 0) {
        mp.rows.forEach((r, i) => {
          const rl = String(r.rowLabel ?? '').trim() || `Linha ${i + 1}`;
          const key = buildMatrixCsvColumnKey(question.question, rl);
          record[key] = r.columnLabel != null ? String(r.columnLabel) : '';
        });
      } else {
        const qk = question.question || '';
        if (qk) {
          record[qk] = getQuestionAnswerPlainText(question);
        }
      }
    });

    return record;
  };
}

/** Identificadores únicos dos questionários citados nas respostas de um envio (vazio → sem nome). */
function questionnaireNameKeys(entry) {
  const keys = new Set();
  entry.answers?.forEach((a) => {
    const n = a.nome != null ? String(a.nome).trim() : '';
    keys.add(n);
  });
  if (keys.size === 0) keys.add('');
  return keys;
}

/** Envios apenas com os blocos de resposta do questionário indicado (útil ao exportar CSV por seção). */
function subsetEntryAnswers(entry, sectionNameKey) {
  const filtered =
    entry.answers?.filter((a) => {
      const n = a.nome != null ? String(a.nome).trim() : '';
      return sectionNameKey === '' ? n === '' : n === sectionNameKey;
    }) ?? [];
  return { ...entry, answers: filtered };
}

/** Agrupa envios por questionário e ordena (nome em pt-BR; mais recentes primeiro dentro do grupo). */
function buildQuestionnaireSections(dataList) {
  const byKey = new Map();

  dataList.forEach((item) => {
    const id = item.id;
    questionnaireNameKeys(item).forEach((nameKey) => {
      if (!byKey.has(nameKey)) {
        byKey.set(nameKey, new Map());
      }
      const m = byKey.get(nameKey);
      if (!id || !m.has(id)) {
        m.set(id || `${nameKey}_${m.size}_${item.timestamp ?? ''}`, item);
      }
    });
  });

  const sections = [...byKey.entries()].map(([nameKey, idMap]) => {
    const items = [...idMap.values()].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
    const displayName =
      nameKey === ''
        ? 'Sem nome do questionário (registros antigos)'
        : nameKey;

    return { nameKey, displayName, items };
  });

  sections.sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));

  return sections;
}

const ExportData = () => {
  const [data, setData] = useState([]);
  const [questionarios, setQuestionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = async () => {
    await auth.authStateReady();
    if (!auth.currentUser || auth.currentUser.isAnonymous) {
      throw new Error('Faça login com e-mail e senha para ver as respostas.');
    }
    const [resultSnapshot, questionarioSnapshot] = await Promise.all([
      getDocs(collection(db, 'results')),
      getDocs(collection(db, 'questionarios')),
    ]);
    const resultList = resultSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const questionarioList = questionarioSnapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    setData(resultList);
    setQuestionarios(questionarioList);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadData();
        setLoading(false);
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setError(err);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const sections = useMemo(() => buildQuestionnaireSections(data), [data]);

  const selectedSection = useMemo(() => {
    if (selectedSectionKey === null) return null;
    return sections.find((s) => s.nameKey === selectedSectionKey) ?? null;
  }, [sections, selectedSectionKey]);

  const totalResponses = data.length;

  const formatDataForCSV = (rows) => {
    const questionColumns = collectCsvColumnPlan(rows);
    const fullOrder = [...CSV_META_KEYS, ...questionColumns];
    const records = [];

    rows.forEach((entry) => {
      let formatted = '';
      if (entry.timestamp) {
        formatted = format(new Date(entry.timestamp), 'dd/MM/yyyy HH:mm');
      }
      const rowBuilder = buildAnswerRow(entry, formatted);
      entry.answers?.forEach((answer) => {
        records.push(rowBuilder(answer));
      });
    });

    const extraKeys = new Set();
    records.forEach((rec) => {
      Object.keys(rec).forEach((k) => {
        if (!fullOrder.includes(k)) extraKeys.add(k);
      });
    });
    const columnOrder = [...fullOrder, ...extraKeys];

    return records.map((rec) => {
      const out = {};
      columnOrder.forEach((k) => {
        out[k] = rec[k] ?? '';
      });
      return out;
    });
  };

  const csvData = formatDataForCSV(data);

  const selectSection = (nameKey) => {
    setSelectedSectionKey(nameKey);
    setExpandedKey(null);
  };

  const goBackToPicker = () => {
    setSelectedSectionKey(null);
    setExpandedKey(null);
  };

  const handleViewResult = (row) => {
    if (!row) return;
    const nameKey =
      row.surveyName && row.surveyName !== 'Sem nome' ? row.surveyName : '';
    setSelectedSectionKey(nameKey);
    if (row.id) {
      setExpandedKey(`${encodeURIComponent(nameKey)}:${row.id}`);
    }
    requestAnimationFrame(() => {
      const el = document.querySelector('.results-detail, .results-picker');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Carregando resultados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>Erro ao carregar dados</h3>
        <p>Não foi possível carregar os resultados. Tente novamente.</p>
      </div>
    );
  }

  const toggleExpand = (storageKey) => {
    setExpandedKey(expandedKey === storageKey ? null : storageKey);
  };

  const closeDeleteModal = () => {
    setPendingDelete(null);
    setAdminPasswordInput('');
  };

  const confirmDelete = async () => {
    if (!ADMIN_PASSWORD) {
      alert('Exclusão não configurada. Defina REACT_APP_ADMIN_RESULTS_PASSWORD no .env.');
      return;
    }
    if (adminPasswordInput !== ADMIN_PASSWORD) {
      alert('Senha incorreta.');
      return;
    }
    if (!pendingDelete) return;

    setIsDeleting(true);
    try {
      if (pendingDelete.type === 'all') {
        for (const row of data) {
          if (row.id) {
            await deleteDoc(doc(db, 'results', row.id));
          }
        }
        setData([]);
        setExpandedKey(null);
        setSelectedSectionKey(null);
      } else if (pendingDelete.type === 'section' && pendingDelete.ids?.length) {
        const idsToDelete = new Set(pendingDelete.ids);
        for (const resultId of idsToDelete) {
          await deleteDoc(doc(db, 'results', resultId));
        }
        setData((prev) => prev.filter((d) => !idsToDelete.has(d.id)));
        setExpandedKey(null);
        if (selectedSectionKey === pendingDelete.sectionNameKey) {
          setSelectedSectionKey(null);
        }
      } else if (pendingDelete.type === 'one' && pendingDelete.id) {
        await deleteDoc(doc(db, 'results', pendingDelete.id));
        setData((prev) => prev.filter((d) => d.id !== pendingDelete.id));
        setExpandedKey(null);
      }
      closeDeleteModal();
    } catch (err) {
      console.error('Erro ao excluir:', err);
      alert('Não foi possível excluir. Verifique permissões do Firestore e tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  const openSectionDelete = (section) => {
    const ids = section.items.map((item) => item.id).filter(Boolean);
    setPendingDelete({
      type: 'section',
      sectionNameKey: section.nameKey,
      displayName: section.displayName,
      ids,
      count: ids.length,
    });
  };

  const deleteModalTitle =
    pendingDelete?.type === 'all'
      ? 'Excluir todas as respostas'
      : pendingDelete?.type === 'section'
        ? 'Excluir respostas desta pesquisa'
        : 'Excluir esta resposta';

  const deleteModalMessage =
    pendingDelete?.type === 'all'
      ? `Esta ação remove permanentemente ${data.length} registro(s) de todas as pesquisas.`
      : pendingDelete?.type === 'section'
        ? `Esta ação remove permanentemente ${pendingDelete.count} resposta(s) da pesquisa "${pendingDelete.displayName}".`
        : 'Esta ação remove permanentemente este registro.';

  return (
    <div className="results-content">
      <ResultadosDashboard
        results={data}
        questionarios={questionarios}
        onViewResult={handleViewResult}
      />

      <div className="results-legacy-section">
        <h2 className="dash-section-title results-legacy-title">Respostas e exportação</h2>
      </div>

      {!ADMIN_PASSWORD && (
        <p className="admin-delete-hint">
          Para habilitar a exclusão de resultados, defina a variável{' '}
          <code>REACT_APP_ADMIN_RESULTS_PASSWORD</code> no arquivo <code>.env</code> na raiz do
          projeto e gere o build novamente. A senha também deve ser protegida por regras de
          segurança no Firebase.
        </p>
      )}

      {ADMIN_PASSWORD && !selectedSection && (
        <div className="admin-delete-actions">
          <button
            type="button"
            className="btn-delete-results-bulk"
            disabled={data.length === 0 || isDeleting}
            onClick={() => setPendingDelete({ type: 'all' })}
          >
            Excluir todas as respostas (todas as pesquisas)
          </button>
        </div>
      )}

      {pendingDelete && (
        <div
          className="results-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          onClick={closeDeleteModal}
        >
          <div className="results-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="delete-modal-title">{deleteModalTitle}</h3>
            <p>{deleteModalMessage}</p>
            <label htmlFor="admin-results-password" className="sr-only">
              Senha de administrador
            </label>
            <input
              id="admin-results-password"
              type="password"
              autoComplete="off"
              placeholder="Senha de administrador"
              value={adminPasswordInput}
              onChange={(e) => setAdminPasswordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmDelete();
              }}
            />
            <div className="results-modal-actions">
              <button type="button" className="results-modal-cancel" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button
                type="button"
                className="results-modal-confirm"
                disabled={isDeleting}
                onClick={confirmDelete}
              >
                {isDeleting ? 'Excluindo…' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="empty-state">
          <h3>Nenhuma resposta ainda</h3>
          <p>As respostas dos questionários aparecerão aqui depois que alguém responder.</p>
        </div>
      ) : !selectedSection ? (
        <div className="results-picker">
          <p className="results-picker-intro">
            {sections.length} pesquisa{sections.length !== 1 ? 's' : ''} com respostas
            {' · '}
            {totalResponses} resposta{totalResponses !== 1 ? 's' : ''} no total
          </p>
          <h2 className="section-title">Escolha uma pesquisa</h2>
          <ul className="results-picker-list">
            {sections.map((section, secIdx) => (
              <li key={section.nameKey || `__none__-${secIdx}`} className="results-picker-item">
                <div className="results-picker-item-info">
                  <span className="results-picker-item-name">{section.displayName}</span>
                  <span className="results-picker-item-count">
                    {section.items.length} resposta{section.items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="results-picker-item-actions">
                  <button
                    type="button"
                    className="btn-view-results"
                    onClick={() => selectSection(section.nameKey)}
                  >
                    Ver respostas
                  </button>
                  {ADMIN_PASSWORD && section.items.length > 0 && (
                    <button
                      type="button"
                      className="btn-delete-section-results"
                      disabled={isDeleting}
                      onClick={() => openSectionDelete(section)}
                    >
                      Excluir respostas
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {csvData.length > 0 && (
            <div className="results-export-all">
              <CSVLink
                data={csvData}
                filename={`resultados_todas_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.csv`}
                className="csv-link"
              >
                <button type="button" className="btn-export-all-csv">
                  Exportar CSV de todas as pesquisas
                </button>
              </CSVLink>
            </div>
          )}
        </div>
      ) : (
        <div className="results-detail">
          <div className="results-detail-header">
            <button type="button" className="btn-back-results" onClick={goBackToPicker}>
              Voltar para lista de pesquisas
            </button>
            <h2 className="results-detail-title">{selectedSection.displayName}</h2>
            <div className="results-detail-meta">
              <span className="results-section-count">
                {selectedSection.items.length} resposta
                {selectedSection.items.length !== 1 ? 's' : ''}
              </span>
              {ADMIN_PASSWORD && selectedSection.items.length > 0 && (
                <button
                  type="button"
                  className="btn-delete-section-results"
                  disabled={isDeleting}
                  onClick={() => openSectionDelete(selectedSection)}
                >
                  Excluir todas desta pesquisa
                </button>
              )}
              {(() => {
                const sectionCsvRows = selectedSection.items.map((e) =>
                  subsetEntryAnswers(e, selectedSection.nameKey)
                );
                const sectionCsv =
                  sectionCsvRows.some((row) => (row.answers?.length ?? 0) > 0) ?
                    formatDataForCSV(sectionCsvRows)
                  : [];
                return (
                  sectionCsv.length > 0 && (
                    <CSVLink
                      data={sectionCsv}
                      filename={`${String(selectedSection.displayName)
                        .slice(0, 60)
                        .replace(/[^\wÀ-ú\s-]/gi, '_')
                        .replace(/\s+/g, '_')
                        .trim() || 'resultados'}_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.csv`}
                      className="csv-link results-section-csv-link"
                    >
                      <button type="button" className="btn-export-section-csv">
                        Exportar CSV desta pesquisa
                      </button>
                    </CSVLink>
                  )
                );
              })()}
            </div>
          </div>

          <div className="responses-list">
            {selectedSection.items.map((item, index) => {
              const storageKey = `${encodeURIComponent(selectedSection.nameKey)}:${item.id || index}`;
              const answersShown =
                item.answers?.filter((a) => {
                  const n = a.nome != null ? String(a.nome).trim() : '';
                  return selectedSection.nameKey === '' ? n === '' : n === selectedSection.nameKey;
                }) ?? [];
              const isExpanded = expandedKey === storageKey;

              return (
                <div key={`${selectedSection.nameKey}-${item.id || index}`} className="response-card">
                  <div className="response-header" onClick={() => toggleExpand(storageKey)}>
                    <div className="response-info">
                      <div className="response-meta">
                        <span className="response-number">Resposta {index + 1}</span>
                        <span className="response-email">
                          {item.email ? `Email: ${item.email}` : 'Anônimo'}
                        </span>
                        {item.timestamp && (
                          <span className="response-date">
                            Data: {format(new Date(item.timestamp), 'dd/MM/yyyy HH:mm')}
                          </span>
                        )}
                        {item.origem && (
                          <span className="response-origem">Origem: {item.origem}</span>
                        )}
                        {item.audioUrl && (
                          <span className="response-audio-badge">Com áudio</span>
                        )}
                      </div>
                      {item.location && (
                        <div className="response-location">
                          Local:{' '}
                          {item.location.city && item.location.city !== 'Não disponível' ? (
                            <>
                              {item.location.city}
                              {item.location.state && `, ${item.location.state}`}
                              {item.location.country && ` - ${item.location.country}`}
                            </>
                          ) : (
                            <>
                              Lat {item.location.latitude?.toFixed(4)}, Long{' '}
                              {item.location.longitude?.toFixed(4)}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className="response-header-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {ADMIN_PASSWORD && item.id && (
                        <button
                          type="button"
                          className="btn-delete-result-item"
                          disabled={isDeleting}
                          onClick={() => setPendingDelete({ type: 'one', id: item.id })}
                        >
                          Excluir
                        </button>
                      )}
                      <button type="button" className="btn-toggle-expand">
                        {isExpanded ? 'Ocultar detalhes' : 'Ver detalhes'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="response-details">
                      {item.origem && (
                        <div className="detail-item">
                          <strong>Origem:</strong> {item.origem}
                        </div>
                      )}

                      {item.ip && (
                        <div className="detail-item">
                          <strong>IP:</strong> {item.ip}
                        </div>
                      )}

                      {item.location && (
                        <div className="location-details">
                          <h4>Detalhes da localização</h4>
                          <div className="location-grid">
                            {item.location.city && (
                              <div className="location-item">
                                <span className="location-label">Cidade:</span>
                                <span className="location-value">{item.location.city}</span>
                              </div>
                            )}
                            {item.location.state && (
                              <div className="location-item">
                                <span className="location-label">Estado:</span>
                                <span className="location-value">{item.location.state}</span>
                              </div>
                            )}
                            {item.location.country && (
                              <div className="location-item">
                                <span className="location-label">País:</span>
                                <span className="location-value">{item.location.country}</span>
                              </div>
                            )}
                            {item.location.latitude && item.location.longitude && (
                              <div className="location-item full-width">
                                <span className="location-label">Coordenadas:</span>
                                <span className="location-value">
                                  {item.location.latitude.toFixed(6)},{' '}
                                  {item.location.longitude.toFixed(6)}
                                </span>
                              </div>
                            )}
                            {item.location.address &&
                              item.location.address !== 'Endereço não disponível' && (
                                <div className="location-item full-width">
                                  <span className="location-label">Endereço:</span>
                                  <span className="location-value">{item.location.address}</span>
                                </div>
                              )}
                          </div>
                        </div>
                      )}

                      {item.audioUrl && (
                        <div className="audio-result-section">
                          <h4>Gravação da entrevista</h4>
                          {item.audioDuration != null && (
                            <p className="audio-result-duration">
                              Duração: {formatRecordingDuration(item.audioDuration)}
                            </p>
                          )}
                          <audio controls preload="metadata" className="audio-result-player">
                            <source src={item.audioUrl} />
                            Seu navegador não suporta reprodução de áudio.
                          </audio>
                          <a
                            href={item.audioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="audio-result-download"
                          >
                            Baixar áudio
                          </a>
                        </div>
                      )}

                      <div className="answers-section">
                        <h4>Respostas desta pesquisa</h4>
                        {answersShown.length ?
                          answersShown.map((answer, ansIndex) => (
                            <div
                              key={ansIndex}
                              className="answer-group questionnaire-answer-block"
                            >
                              {answer.questions?.map((question, qIndex) => (
                                <div key={qIndex} className="answer-item">
                                  <div className="question-text">
                                    <strong>{question.question}</strong>
                                  </div>
                                  <div className="answer-text">
                                    {getQuestionAnswerPlainText(question)}
                                  </div>
                                </div>
                              ))}
                              {!answer.questions?.length && (
                                <p className="questionnaire-result-empty">
                                  Nenhuma pergunta neste bloco.
                                </p>
                              )}
                            </div>
                          ))
                        : <p className="questionnaire-result-empty">
                            Nenhuma resposta deste formulário neste registro — pode ser dados
                            antigos ou inconsistentes.
                          </p>
                        }
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Resultados = () => {
  return (
    <div className="page-content-card results-page-inner">
      <ExportData />
    </div>
  );
};

export default Resultados;
